import React, { useContext, useMemo, useState } from 'react';
import { TranslationKey } from './../../../../../../../../types/translation/TranslationKey';
import {
  ProductTransaction,
  ProductTransactionStatus,
  ProductTransactionType,
} from '../../../../../../../../types/productTransaction';
import { Grid } from '@mui/material';
import DropdownSelect from '../../../../../../Common/DropdownSelect';
import { ProductMasterData } from '../../../../../../../../types/product_master_data/productMasterData';
import { OrderParentType } from '../../../../../../../../types/order';
import { testIds } from '../../../../../../../../util/identifiers/identifiers.util';
import { t, translate } from '../../../../../../../../types/translation/Translator';
import { OrderContext } from '../../../../../../../../context/OrderContext';
import { ProductContext } from '../../../../../../../../context/ProductContext';
import { Container } from '../../../../../../../../types/container';
import { ContainerContext } from '../../../../../../../../context/ContainerContext';
import { CompanyContext } from '../../../../../../../../context/CompanyContext';
import { DropdownSearchSelect } from '../../../../../../../components/Dropdown';
import CreateOrderCreateContainerModal from '../../Modals/CreateOrderCreateContainerModal';
import { IconButton } from '../../../../../../../icons';
import { IconType } from '../../../../../../../icons/Icon';
import { isValidSourceContainer } from '../../../../../../../../util/containers.util';
import { UnitOfMeasure } from '../../../../../../../../types/uom';
import { HandlingUnitContext } from '../../../../../../../../context/HandlingUnitContext';
import { ProductMasterDataContext } from '../../../../../../../../context/ProductMasterDataContext';
import { SystemBin } from '../../../../../../../../types/bin';

interface ProductTransactionSourceContainerProps {
  transaction: ProductTransaction;
  setTransaction: React.Dispatch<React.SetStateAction<ProductTransaction>>;

  productMasterData?: ProductMasterData;
  container?: Container;

  disabled?: boolean;
}

export default function ProductTransactionSourceContainer({
  transaction,
  setTransaction,
  productMasterData: pmd,
  container,
  disabled = false,
}: ProductTransactionSourceContainerProps) {
  if (!pmd) return <ContainerTransactionSourceContainer container={container} />;

  const { currentCompany } = useContext(CompanyContext);
  const { orders } = useContext(OrderContext);
  const { containers } = useContext(ContainerContext);
  const { products } = useContext(ProductContext);
  const { handlingUnits } = useContext(HandlingUnitContext);
  const { productMasterData } = useContext(ProductMasterDataContext);

  const [openCreateContainerModal, setOpenCreateContainerModal] = useState(false);

  const showContainer = useMemo(() => currentCompany.settings.featureToggles.containers.containers, [currentCompany]);

  const selectedHandlingUnit = useMemo(() => {
    return handlingUnits.get(transaction.product.handlingUnitId || '');
  }, [handlingUnits, transaction.product.handlingUnitId]);

  const sourceProductMasterDataId = useMemo(() => {
    return selectedHandlingUnit?.parentId || pmd.id;
  }, [selectedHandlingUnit, pmd.id]);

  const handlingUnitProductMasterData = useMemo(() => {
    if (!selectedHandlingUnit) return undefined;
    return productMasterData.get(selectedHandlingUnit.parentId);
  }, [selectedHandlingUnit]);

  const containersForProduct = useMemo(() => {
    const quantityByContainer = new Map<string, number>();
    [...(products.get(sourceProductMasterDataId)?.values() || [])].forEach(p => {
      quantityByContainer.set(p.containerId || p.binId, parseFloat(p.unitQuantity || '0') || 0);
    });
    return quantityByContainer;
  }, [products, pmd, selectedHandlingUnit, sourceProductMasterDataId]);

  if (transaction.type === ProductTransactionType.inbound || !showContainer) return null;

  const isDisabled =
    transaction.status !== ProductTransactionStatus.created ||
    disabled ||
    (orders.get(transaction.parentId || '')?.parentType === OrderParentType.task &&
      transaction.type === ProductTransactionType.outbound) ||
    !!container ||
    !!transaction.containerTransactionId ||
    pmd.serialManaged ||
    (pmd.lpnManaged && !pmd.lotManaged);

  const validContainers = [...containers.values()]
    .filter(c =>
      isValidSourceContainer({
        container: c,
        transaction,
        products: [...(products.get(sourceProductMasterDataId)?.values() || [])].flat() || [],
        productMasterData: pmd,
      }),
    )
    .sort((a, b) => (containersForProduct.get(b.id) || 0) - (containersForProduct.get(a.id) || 0));

  const displayQuantity = (quantity: number) => {
    if (!selectedHandlingUnit) {
      return UnitOfMeasure.toQuantityWithSuffix(pmd.unitOfMeasure, quantity.toString());
    }

    const multiplier = selectedHandlingUnit.quantity;
    const handlingUnitQuantity = UnitOfMeasure.toQuantityWithSuffix(
      handlingUnitProductMasterData?.unitOfMeasure,
      quantity.toString(),
    );
    const handledUnitQuantity = UnitOfMeasure.toQuantityWithSuffix(
      pmd.unitOfMeasure,
      (quantity * multiplier).toString(),
    );
    return `${handlingUnitQuantity} (${handledUnitQuantity})`;
  };

  return (
    <>
      <CreateOrderCreateContainerModal
        open={openCreateContainerModal}
        setOpen={setOpenCreateContainerModal}
        onCreated={c => setTransaction(transaction => transaction.withFromContainerId(c?.id))}
      />
      <Grid item xs={6}>
        <div className='flex h-full items-center gap-1'>
          <div className='flex-grow'>
            <DropdownSearchSelect
              disabled={disabled || isDisabled}
              placeholder={t(TranslationKey.sourceContainer).singular.label}
              label={t(TranslationKey.sourceContainer).singular.label}
              items={validContainers}
              value={
                containers.get(transaction.product.fromContainerId || '') ||
                (transaction.status !== ProductTransactionStatus.created ? transaction.metadata?.fromContainer : null)
              }
              onSelect={container => {
                setTransaction(transaction => {
                  transaction = transaction.withFromContainerId(container?.id);
                  if (!container) return transaction;
                  if (currentCompany.settings.featureToggles.containers.allowBinMismatch) return transaction;
                  if (container.binId === SystemBin.id) return transaction;

                  transaction.product.fromBinId = container.binId;
                  return transaction;
                });
              }}
              textBuilder={item => `${item?.identifier}`}
              itemBuilder={item => (
                <Grid container>
                  <Grid item flexGrow={1}>
                    <p>{item?.identifier}</p>
                  </Grid>
                  <Grid item my={'auto'}>
                    <p className='text-sm text-gray-400'>
                      {displayQuantity(containersForProduct.get(item?.id || '') ?? 0)}
                    </p>
                  </Grid>
                </Grid>
              )}
            />
          </div>
          {!transaction.product.toContainerId && !disabled && !isDisabled ? (
            <div className='flex h-full items-center pt-6'>
              <IconButton
                className='border border-gray-default'
                type={IconType.plus}
                onClick={() => setOpenCreateContainerModal(true)}
                text={translate({ key: TranslationKey.createContainer })}
              />
            </div>
          ) : null}
        </div>
      </Grid>
    </>
  );
}

interface ContainerTransactionSourceContainerProps {
  container: Container | undefined;
}

function ContainerTransactionSourceContainer({ container }: ContainerTransactionSourceContainerProps) {
  if (!container) return null;

  const { containers } = useContext(ContainerContext);

  return (
    <Grid item xs={6}>
      <DropdownSelect
        testId={testIds.container}
        disabled
        placeholder={t(TranslationKey.sourceContainer).singular.label}
        label={t(TranslationKey.sourceContainer).singular.label}
        values={[]}
        selectedValue={containers.get(container.id) || null}
        onChange={() => {}}
        toText={item => item.identifier}
      />
    </Grid>
  );
}
